import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

const apiUrl = `${window.location.protocol}//${window.location.hostname}/api`;

const CustomerSetup = ({ onComplete }) => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [accountInfo, setAccountInfo] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('No invite token provided.');
            setLoading(false);
            return;
        }

        const validate = async () => {
            try {
                const res = await fetch(`${apiUrl}/portal/invite/${token}`);
                const data = await res.json();
                if (res.ok) {
                    setAccountInfo(data);
                } else {
                    setError(data.message || 'Invalid invite link.');
                }
            } catch (e) {
                setError('Failed to validate invite link.');
            }
            setLoading(false);
        };
        validate();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(`${apiUrl}/portal/setup-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password })
            });
            const data = await res.json();
            if (res.ok) {
                onComplete();
            } else {
                setError(data.message || 'Failed to set password.');
            }
        } catch (err) {
            setError('Failed to set password.');
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5' }}>
            <Card sx={{ width: 420, mx: 2 }}>
                <Box sx={{ backgroundColor: '#0b355d', color: 'white', p: 3, textAlign: 'center' }}>
                    <Typography variant="h5">Pacific Landscape Supply</Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>Set Up Your Account</Typography>
                </Box>
                <CardContent sx={{ p: 3 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {accountInfo ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Welcome, {accountInfo.contactName || 'there'}! Set a password for your {accountInfo.customerName} account.
                            </Typography>

                            <form onSubmit={handleSubmit}>
                                <TextField fullWidth label="Email" value={accountInfo.email} margin="normal" disabled />
                                <TextField fullWidth label="Password" type="password" value={password}
                                    onChange={e => setPassword(e.target.value)} margin="normal" required
                                    helperText="At least 6 characters" />
                                <TextField fullWidth label="Confirm Password" type="password" value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)} margin="normal" required />
                                <Button fullWidth variant="contained" type="submit" disabled={submitting} sx={{ mt: 2, backgroundColor: '#0b355d' }}>
                                    {submitting ? 'Setting password...' : 'Set Password'}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                            This invite link is invalid or has expired. Please contact Pacific Landscape Supply for a new invite.
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default CustomerSetup;
